import React from 'react';
import { useSelector } from 'react-redux';

import classNames from 'classnames';

import { IsNotCAP } from 'components/CAP/IsNotCAP/IsNotCAP';
import { Tooltip } from 'components/Tooltip/Tooltip';
import {
  NOOP,
  OPP_UNIONPAY_CAMPAIGN,
  RECOMMENDED_OFFERS_FOR_INDIA,
  SBI_CAMPAIGN_OPTIONS,
  SINGAPUR_CAMPAING_OPTIONS,
} from 'constants/index';
import { ReactComponent as InfoIcon } from 'icons/ico-info-current-color.svg';
import type { Currency, Fee, Logo, Price } from 'models';
import { isEmbedded, isPayingFromChina, isPayingFromSingapur } from 'selectors';
import { useTranslations } from 'utils';
import { getCardinalNumber } from 'utils/date/date';
import { formatAmount } from 'utils/money';

import { Offer } from './components/Offer/Offer';
import { RecurringInfo } from './components/Offer/components/RecurringInfo/RecurringInfo';

const TRACKER_SELECT_VALUE = 'select';
const TRACKER_MORE_INFO_VALUE = 'show more';

type OptionProps = {
  chargeDay?: number;
  currency: Currency;
  description?: string;
  exchangeRate?: number;
  fee?: Omit<Fee, 'id'>;
  id: string;
  interval?: number;
  logo?: Logo;
  logos?: Logo[];
  markupRate?: number;
  name: string;
  onSelect?: (id: string) => void;
  payingFromIndiaInUSD?: boolean;
  paymentMethod: string;
  paymentOption: string;
  price: Price;
  recommended?: boolean;
  requiresDocuments?: boolean;
  trackCheckMarkup: () => void;
  trackHeapSelectedPaymentOption?: () => void;
  trackPaymentOption: (paymentOption: string, value: string) => void;
  unfoldImportantInfo: boolean;
  redeemRewards?: boolean;
  recurring?: boolean;
};

function Option({
  chargeDay,
  currency,
  description,
  exchangeRate,
  fee,
  id,
  interval,
  logo,
  logos = [],
  markupRate,
  name,
  onSelect,
  payingFromIndiaInUSD,
  paymentMethod,
  paymentOption,
  price,
  recommended = false,
  requiresDocuments = false,
  trackCheckMarkup = NOOP,
  trackHeapSelectedPaymentOption = NOOP,
  trackPaymentOption = NOOP,
  unfoldImportantInfo,
  redeemRewards,
  recurring,
}: OptionProps) {
  const i18n = useTranslations();
  const { value: priceValue } = price;
  const feeValue = fee?.value || 0;
  const hasFee = feeValue > 0;
  const hasFx = markupRate && exchangeRate;
  const feeWithFormat = formatAmount(feeValue, currency);
  const priceWithFormat = formatAmount(priceValue, currency);
  const {
    Actions,
    Body,
    Content,
    Description,
    FXInfo,
    Fee,
    Label,
    LogosList,
    MoreInfo,
    Name,
    PaymentMethodLogo,
    Price,
    Recommended,
  } = Offer;

  const embedded: boolean = useSelector(isEmbedded);
  const isFromSingapur = useSelector(isPayingFromSingapur);
  const showRedeemRewardsLabel = redeemRewards && !embedded;

  function handleMoreInfoOnToggle() {
    trackPaymentOption(paymentOption, TRACKER_MORE_INFO_VALUE);
  }

  function handleFXInfoOnToggle() {
    trackCheckMarkup();
  }

  function handleOnSelect(id: string) {
    trackPaymentOption(paymentOption, TRACKER_SELECT_VALUE);
    trackHeapSelectedPaymentOption();
    onSelect?.(id);
  }

  function isUnionPayRecommendedOption() {
    return OPP_UNIONPAY_CAMPAIGN.includes(paymentOption) && !embedded;
  }

  function isSBIRecommendedOption() {
    return SBI_CAMPAIGN_OPTIONS.includes(paymentOption) && !embedded;
  }

  function recommendedForIndia() {
    return (
      payingFromIndiaInUSD &&
      RECOMMENDED_OFFERS_FOR_INDIA.includes(paymentOption)
    );
  }

  function isRecommendedForSingapur() {
    return (
      isFromSingapur &&
      SINGAPUR_CAMPAING_OPTIONS.includes(paymentOption) &&
      !embedded
    );
  }

  function isRecommended() {
    return (
      recommended ||
      isUnionPayRecommendedOption() ||
      isSBIRecommendedOption() ||
      recommendedForIndia() ||
      isRecommendedForSingapur()
    );
  }

  function recommendationText() {
    if (recommended) {
      return 'paymentOptions.recommended';
    }

    if (isSBIRecommendedOption()) {
      return 'paymentOptions.recommended.newAndExclusive';
    }

    return 'paymentOptions.recommended.fastAndMostPopular';
  }

  const feeComponent = hasFee && <Fee value={feeWithFormat} />;
  const isRecurring = recurring !== undefined;

  return (
    <Offer
      i18n={i18n}
      id={id}
      name={name}
      currency={currency}
      fee={fee}
      price={price}
      className={classNames(isRecommended() ? 'Offer--recommended' : null)}
      recurring={recurring}
    >
      {isRecommended() && (
        <Recommended>
          <span className="Offer--recommended-desktopTitle">
            {i18n.t(recommendationText())}
          </span>
          <span className="Offer--recommended-mobileTitle">
            {i18n.t('paymentOptions.recommended.mobile')}
          </span>
        </Recommended>
      )}
      <Body>
        <PaymentMethodLogo logo={logo} paymentMethod={paymentMethod} />
        <Content>
          <Name value={name} />
          <Price>
            {priceWithFormat}{' '}
            {interval && (
              <span>
                {i18n.t('paymentOptions.recurringPayment.interval', {
                  interval: `${interval}`,
                })}
                {chargeDay && (
                  <>
                    {', '}
                    <span className="Offer-recurringPayment-chargeDay">
                      {i18n.t('paymentOptions.recurringPayment.chargedDate', {
                        chargeDayOrdinal: `${getCardinalNumber(
                          chargeDay,
                          i18n.currentLocale,
                        )}`,
                        chargeDay: `${chargeDay}`,
                      })}
                      <Tooltip
                        className="bg-transparent"
                        defaultMessage={
                          i18n.t(
                            'paymentOptions.recurringPayment.chargedDate.tooltip',
                          ) as string
                        }
                      >
                        <InfoIcon
                          height="12"
                          width="12"
                          className="marginRight-xs"
                          aria-hidden="true"
                        />
                      </Tooltip>
                    </span>
                  </>
                )}
              </span>
            )}
          </Price>
          {requiresDocuments && (
            <span className="Offer--requiresDocumentation">
              <Tooltip
                defaultMessage={
                  i18n.t(
                    'paymentOptions.requiresDocumentation.tooltip',
                  ) as string
                }
                className="Offer--requiresDocumentation-tooltip"
              >
                <InfoIcon
                  height="12"
                  width="12"
                  className="marginRight-xs"
                  aria-hidden="true"
                />
                {i18n.t('paymentOptions.requiresDocumentation')}
              </Tooltip>
            </span>
          )}

          <IsNotCAP>{feeComponent}</IsNotCAP>

          {hasFx && (
            <FXInfo
              markupRate={markupRate}
              exchangeRate={exchangeRate}
              onToggle={handleFXInfoOnToggle}
            />
          )}
        </Content>
      </Body>
      <LogosList logos={logos} />
      <div className="Offer-actionsContainer">
        <Actions onSelect={handleOnSelect} />
        {isRecurring && <RecurringInfo />}
        {showRedeemRewardsLabel && (
          <Label value={i18n.t('paymentOptions.redeemRewards')} />
        )}
      </div>

      {description && (
        <MoreInfo
          onToggle={handleMoreInfoOnToggle}
          initialOpen={unfoldImportantInfo}
        >
          <Description value={description} />
        </MoreInfo>
      )}
    </Offer>
  );
}

export { Option, type OptionProps };
